import { useContext, useEffect, useState } from "react";
import type {
  AttributeSchema,
  PortfolioControlsSchema,
} from "../../../../../types/types.PIM";
import Axios from "axios";
import { H4 } from "../../../../../components/Typography/Typography";
import { Form } from "../../../../../layout/FormLayout";
import { Controller } from "react-hook-form";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { ConfirmOrCancelButtonContainer } from "../../../../../components/Buttons/Buttons";
import { useFormWrapper, useStoreState } from "../../../../../util/util";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { InfoBlockSmall } from "../../../../../components/InfoBlocks/InfoBlocks";
import type { OptionType } from "../../../../../types/types";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import ReactTooltip from "react-tooltip";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { strings } from "../../../../../util/strings";

interface FormInputs {
  attribute: OptionType;
  is_searchable: true;
  is_filterable: true;
}

export const PortfolioControlForm = ({
  portfolioControl,
  handleFormSubmit,
  portfolioControlsData,
  productSelectors,
}: {
  portfolioControl?: PortfolioControlsSchema;
  handleFormSubmit: () => void;
  portfolioControlsData?: PortfolioControlsSchema[];
  productSelectors?: { name: string; order: string }[];
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { notifyError, notifySuccess } = useContext(Notifications);
  const {
    storefront_id,
    storefront_metadata: { default_language },
    tenant_id,
  } = useStoreState();
  const [controlIsFilterable, setControlIsFilterable] = useState(
    portfolioControl?.is_filterable || false
  );
  const [controlIsSearchable, setControlIsSearchable] = useState(
    portfolioControl?.is_searchable
  );

  const { t } = useTranslation();
  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(
      z.object({
        attribute: z.object({
          value: z.string().min(1, strings(t).thisIsARequiredField),
          label: z.string().min(1, strings(t).thisIsARequiredField),
          display_name: z.string(),
          list_id: z.string().nullable(),
          is_filterable: z.boolean(),
        }),
      })
    ),
    defaultValues: {
      attribute: portfolioControl?.attribute
        ? {
            label: portfolioControl.attribute.name,
            value: portfolioControl.attribute?.id,
            display_name:
              portfolioControl.attribute.display_name ||
              portfolioControl.attribute.name,
            list_id: portfolioControl.attribute.list_id,
            is_filterable: portfolioControl.attribute.is_filterable,
          }
        : {
            label: undefined,
            value: undefined,
            display_name: undefined,
            list_id: undefined,
            is_filterable: undefined,
          },
    },
    reValidateMode: "onChange",
  });

  const { handleSubmit, control, formState, errors, watch } = methodsOfUseForm;

  const handleIsActiveClick = () => {
    setControlIsFilterable((prev) => !prev);
  };

  const handleIsSearchableClick = () => {
    setControlIsSearchable((prev) => !prev);
  };

  const onSubmit = async (inputs: FormInputs) => {
    const formData = {
      attribute_id: inputs.attribute.value,
      is_searchable: controlIsSearchable,
      is_filterable: controlIsFilterable,
    };
    setSubmitting(true);
    try {
      portfolioControl &&
      inputs.attribute.value === portfolioControl.attribute.id
        ? await Axios.patch(
            `v2/storefronts/${storefront_id}/portfolio-controls/${portfolioControl?.id}`,
            formData
          )
        : await Axios.post(
            `v2/storefronts/${storefront_id}/portfolio-controls`,
            formData
          );
      notifySuccess(t("Your changes have been saved successfully"));
      setSubmitting(false);
      handleFormSubmit();
    } catch (error) {
      notifyError(t("Something went wrong, please try again"), { error });
      setSubmitting(false);
    }
  };
  const selectedAttribute = watch("attribute");

  useEffect(() => {
    setControlIsFilterable(() => portfolioControl?.is_filterable || false);
    setControlIsSearchable(() => portfolioControl?.is_searchable || false);
  }, [portfolioControl?.is_filterable, portfolioControl?.is_searchable]);

  return (
    <>
      <H4 style={{ marginBottom: 20 }}>
        {portfolioControl ? t("Edit Attribute") : t("Add Attribute")}
      </H4>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div>
          <Controller
            as={SearchSelectInfiniteScroll}
            control={control}
            name="attribute"
            placeholder={t("Select Attribute")}
            baseUrl={`/v2/tenants/${tenant_id}/pim/attributes`}
            getOptions={(attributes: AttributeSchema[]) => {
              const attributeOptions = attributes.map((attribute) => ({
                value: attribute.id,
                label: attribute.name,
                display_name: attribute.display_name || attribute.name,
                list_id: attribute.list_id,
                is_filterable: attribute.is_filterable,
              }));

              if (!portfolioControlsData) {
                return attributeOptions;
              }

              const existingNames = new Set(
                portfolioControlsData.map((control) => control.attribute.name)
              );

              return attributeOptions.filter(
                (option) => !existingNames.has(option.label)
              );
            }}
            errors={errors}
            disabled={!!portfolioControl}
            formState={formState}
            defaultOptions
          />
        </div>
        {selectedAttribute.value && (
          <>
            <InfoBlockSmall
              header={t(`Display Name (${default_language.toUpperCase()})`)}
              content={selectedAttribute.display_name}
            />
            <ToggleSwitchV2
              name={`is_searchable`}
              label={t("Search")}
              onChange={handleIsSearchableClick}
              checked={controlIsSearchable}
            />
            {selectedAttribute.is_filterable && (
              <>
                <ToggleSwitchV2
                  name={`is_filterable`}
                  label={t("Filter")}
                  onChange={handleIsActiveClick}
                  checked={controlIsFilterable}
                  disabled={
                    productSelectors
                      ?.map((selector) => selector.name)
                      .indexOf(
                        portfolioControl?.attribute?.new_object_type?.toLocaleLowerCase() ||
                          ""
                      ) !== -1
                  }
                  tooltip={
                    productSelectors
                      ?.map((selector) => selector.name)
                      .indexOf(
                        portfolioControl?.attribute?.new_object_type?.toLocaleLowerCase() ||
                          ""
                      ) !== -1
                      ? t(
                          "You cannot switch this attribute off as it's used as a product selector"
                        )
                      : undefined
                  }
                />
                <ReactTooltip id="delete-attribute-tooltip" place="top" />
              </>
            )}
          </>
        )}
        <ConfirmOrCancelButtonContainer
          confirmText={t("Save")}
          isConfirmLoading={submitting}
          onConfirm={handleSubmit(onSubmit)}
          onCancel={handleFormSubmit}
        />
      </Form>
    </>
  );
};
